import {
	Collapse,
	createStyles,
	Grid,
	InputAdornment,
	TextField,
	Theme,
	withStyles,
	WithStyles,
} from '@material-ui/core'
import React from 'react'
import {
	IncentiveEarned,
	IncentiveEarnedItems,
	IncentiveType,
	IncentiveTypeItems,
	StipendFrequency,
	StipendFrequencyItems,
	WageFrequency,
	WageFrequencyItems
} from '../../../services/Model/Outcome'
import OutcomeDropDownItem from '../../FormTemplates/CustomOutcomeDropDown/OutcomeDropDownItem'

interface IncentiveEarnedSectionProps extends WithStyles<typeof styles> {
	incentiveEarned?: IncentiveEarned | null
	onIncentiveEarnedChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
	incentiveType?: IncentiveType | null
	onIncentiveTypeChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
	stipendAmount?: number | null
	stipendFrequency?: StipendFrequency | null
	onStipendAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	onStipendFrequencyChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
	wageAmount?: number | null
	wageFrequency?: WageFrequency | null
	onWageAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	onWageFrequencyChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  amtRequired?:boolean
  serviceType?: string
}

const IncentiveEarnedSection: React.FC<IncentiveEarnedSectionProps> = (props: IncentiveEarnedSectionProps) => {
	const {
		classes,
		incentiveEarned,
		onIncentiveEarnedChange,
		incentiveType,
		onIncentiveTypeChange,
		stipendAmount,
		stipendFrequency,
		onStipendAmountChange,
		onStipendFrequencyChange,
		wageAmount,
		wageFrequency,
		onWageAmountChange,
		onWageFrequencyChange,
    serviceType,
	} = props

	const [openIncentive, setIncentiveOpen] = React.useState(props.incentiveEarned === IncentiveEarned.INCENTIVE)
	const [openStipend, setStipendOpen] = React.useState(props.incentiveEarned === IncentiveEarned.STIPEND)
	const [openWage, setWageOpen] = React.useState(props.incentiveEarned === IncentiveEarned.WAGE)

	React.useEffect(() => {
		setIncentiveOpen(incentiveEarned === IncentiveEarned.INCENTIVE)
	}, [incentiveEarned])

	React.useEffect(() => {
		setStipendOpen(incentiveEarned === IncentiveEarned.STIPEND)
	}, [incentiveEarned])

	React.useEffect(() => {
		setWageOpen(incentiveEarned === IncentiveEarned.WAGE)
	}, [incentiveEarned])

	const earned = IncentiveEarnedItems.find((i) => i.value === incentiveEarned)

	const getStipendFrequencyLabel = (frequency?: StipendFrequency | null) => {
		if (!stipendFrequency) return undefined

		const item = StipendFrequencyItems.find((i) => i.value === frequency)

		return item ? item.label : undefined
	}

	const getWageFrequencyLabel = (frequency?: WageFrequency | null) => {
		if (!frequency) return undefined

		const item = WageFrequencyItems.find((i) => i.value === frequency)

		return item ? item.label : undefined
	}


	const incentiveTypeItem = IncentiveTypeItems.find((i) => i.value === incentiveType || i.label === incentiveType) || null

	return (
		<div className={classes.root}>
			<OutcomeDropDownItem
				label="Incentive Earned"
				prop="incentiveEarned"
				value={earned ? earned.label : ''}
				onInputChange={onIncentiveEarnedChange}
				items={IncentiveEarnedItems.map((i) => i.label)}
        serviceType={serviceType}
			/>
			<Collapse
				in={openIncentive}
				style={{ transformOrigin: '0 0 0', flexGrow: 1 }} {...(openIncentive ? { timeout: 750 } : {})}>
				<OutcomeDropDownItem
					label="Incentive Type"
					prop="incentiveType"
					value={incentiveTypeItem ? incentiveTypeItem.label : ''}
					onInputChange={onIncentiveTypeChange}
					items={IncentiveTypeItems.map((i) => i.label)}
          required={false}
				/>
			</Collapse>
			<Collapse
				in={openStipend}
				style={{ transformOrigin: '0 0 0', flexGrow: 1 }} {...(openStipend ? { timeout: 750 } : {})}>
				<Grid container={true} spacing={8}>
					<Grid item={true} xs={12} md={3} className={classes.dropdownLabel}>
						Total Stipend Amount&#58;  { openStipend === true && (<span className={classes.requiredField} >*</span> ) }
					</Grid>
					<Grid item={true} xs={12} sm={3}>
						<TextField
							name="stipendAmount"
							className={classes.dropdown}
							required={(props.amtRequired===false)?false: openStipend}
							value={stipendAmount || ''}
							onChange={onStipendAmountChange}
							margin="dense"
							variant="outlined"
							InputProps={{
								startAdornment: <InputAdornment position="start">$</InputAdornment>,
								inputProps: {
									type: 'number',
									min: '0',
									step: '0.01'
								},
							}}
						/>
					</Grid>
				</Grid>
				<OutcomeDropDownItem
					label="Stipend Frequency"
					prop="stipendFrequency"
					value={getStipendFrequencyLabel(stipendFrequency)}
					onInputChange={onStipendFrequencyChange}
					items={StipendFrequencyItems.map((i) => i.label)}
				/>
			</Collapse>
			<Collapse in={openWage} style={{ transformOrigin: '0 0 0', flexGrow: 1 }} {...(openWage ? { timeout: 750 } : {})}>
				<Grid container={true} spacing={8}>
					<Grid item={true} xs={12} md={3} className={classes.dropdownLabel}>
						Wage Amount&#58; { openWage === true && (<span className={classes.requiredField} >*</span> ) }
					</Grid>
					<Grid item={true} xs={12} sm={3}>
						<TextField
							required={(props.amtRequired===false)?false: openWage}
							name="wageAmount"
							className={classes.dropdown}
							value={wageAmount || ''}
							onChange={onWageAmountChange}
							margin="dense"
							variant="outlined"
							InputProps={{
								startAdornment: <InputAdornment position="start">$</InputAdornment>,
								inputProps: {
									type: 'number',
									min: '0',
									step: '0.01'
								},
							}}
						/>
					</Grid>
				</Grid>
				<OutcomeDropDownItem
					label="Wage Frequency"
					prop="wageFrequency"
					value={getWageFrequencyLabel(wageFrequency)}
					onInputChange={onWageFrequencyChange}
					items={WageFrequencyItems.map((i) => i.label)}
				/>
			</Collapse>
		</div>
	)
}

const styles = (theme: Theme) =>
	createStyles({
		root: {},
		dropdown: { display: 'flex', flex: 1 },
		dropdownLabel: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'flex-end',
			[theme.breakpoints.down('sm')]: {
				justifyContent: 'flex-start',
			},
		},
    requiredField:{
      color:'red'
    }
	})

export default withStyles(styles)(IncentiveEarnedSection)
